import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { EnterpriseLocation, EnterpriseMember } from 'models/Enterprises';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useMemberLocations from 'hooks/useMemberLocations';
import { isSingleElementArray } from 'utils';
import { Loader } from 'ncoded-component-library';
import EditOption from 'components/EditOption';
import AddNewOption from 'components/AddNewOption';
import EditEntepriseValueModal from 'components/EditEntepriseValueModal';
import { FormApi } from 'final-form';
import api from 'api';
import DeleteMemberLocationModal from 'components/DeleteMemberLocationModal';

import './MemberLocationForm.styles.scss';

type MemberLocationFormProps = {
  className?: string;
  enterpriseId: string;
  memberLocations: EnterpriseLocation[];
  updateMemberLocations: (
    updatedMemberLocations: EnterpriseLocation[],
    shouldRefetch?: boolean,
  ) => void;
};

const MemberLocationForm: React.FC<MemberLocationFormProps> = (props) => {
  const { className, enterpriseId, memberLocations, updateMemberLocations } =
    props;

  const [isLoading, setIsLoading] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [unitId, setUnitId] = useState('');
  const [newValue, setNewValue] = useState('');
  const [oldValue, setOldValue] = useState('');
  const [enterpriseMembersWithLocations, setEnterpriseMembersWithLocations] =
    useState<EnterpriseMember[]>([]);

  const { popServerError } = useContext(PopNotificationsContext);

  const [editLocationModal, editLocationModalRef] = useCallbackRef<ModalRef>();
  const [deleteLocationModal, deleteLocationModalRef] =
    useCallbackRef<ModalRef>();

  const { sortedMemberLocations } = useMemberLocations(memberLocations);

  const showDelete = useMemo(
    () => !isSingleElementArray(memberLocations),
    [memberLocations],
  );

  const classes = classNames('member-location-form', className);

  const { t } = useTranslation();

  const getInitialValues = useCallback(
    (_id: string, name: string) => ({ _id, name }),
    [],
  );

  const resetStates = useCallback(() => {
    setUnitId('');
    setNewValue('');
    setOldValue('');
    setEnterpriseMembersWithLocations([]);
  }, []);

  const onAdd = useCallback(
    async ({ name }, formApi: FormApi) => {
      try {
        setIsLoading(true);
        const { data } = await api.enterprises.createMemberLocation(
          enterpriseId,
          name,
        );
        updateMemberLocations([...memberLocations, data], false);
        formApi.reset();
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [enterpriseId, memberLocations, popServerError, updateMemberLocations],
  );

  const editValue = useCallback(
    async (locationId: string, name: string) => {
      try {
        setIsLoading(true);
        await api.enterprises.updateMemberLocation(locationId, name);
        setEditingValue('');
        updateMemberLocations(
          memberLocations.map((location) =>
            location._id === locationId ? { ...location, name } : location,
          ),
        );
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [memberLocations, popServerError, updateMemberLocations],
  );

  const onEdit = useCallback(
    async ({ _id, name }) => {
      try {
        setIsLoading(true);

        const { data: enterpriseMembersWithLocations } =
          await api.enterprises.getEnterpriseMembersForLocation(_id);

        if (enterpriseMembersWithLocations.length) {
          setEnterpriseMembersWithLocations(enterpriseMembersWithLocations);
          setOldValue(
            memberLocations.find((location) => location._id === _id).name,
          );
          setNewValue(name);
          setUnitId(_id);
          setIsLoading(false);
          editLocationModal?.open();
        } else {
          editValue(_id, name);
        }
      } catch (e) {
        popServerError(e);
        setIsLoading(false);
      }
    },
    [editLocationModal, editValue, memberLocations, popServerError],
  );

  const onEditConfirm = useCallback(
    () => editValue(unitId, newValue),
    [editValue, newValue, unitId],
  );

  const onDelete = useCallback(
    async (_id) => {
      try {
        setIsLoading(true);

        const { data: enterpriseMembersWithLocations } =
          await api.enterprises.getEnterpriseMembersForLocation(_id);

        if (enterpriseMembersWithLocations.length) {
          setUnitId(_id);
          setEnterpriseMembersWithLocations(enterpriseMembersWithLocations);
          deleteLocationModal?.open();
        } else {
          const { data } = await api.enterprises.deleteMemberLocation(_id);
          updateMemberLocations(data, false);
        }
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [deleteLocationModal, popServerError, updateMemberLocations],
  );

  return (
    <div className={classes}>
      {isLoading && <Loader />}

      {sortedMemberLocations.map(({ _id, name }) => (
        <EditOption
          key={_id}
          value={name}
          isEditing={editingValue === _id}
          showDelete={showDelete}
          initialValues={getInitialValues(_id, name)}
          onPencilClick={() => setEditingValue(_id)}
          onCancel={() => setEditingValue('')}
          onDelete={() => onDelete(_id)}
          onEdit={onEdit}
        />
      ))}

      <AddNewOption onAdd={onAdd} />

      <EditEntepriseValueModal
        ref={editLocationModalRef}
        enterpriseMembersWithValue={enterpriseMembersWithLocations}
        dataType={t('location')}
        oldValue={oldValue}
        newValue={newValue}
        onClose={resetStates}
        onConfirm={onEditConfirm}
      />

      <DeleteMemberLocationModal
        ref={deleteLocationModalRef}
        memberLocations={memberLocations}
        enterpriseLocationId={unitId}
        enterpriseMembersWithLocation={enterpriseMembersWithLocations}
        onClose={resetStates}
        updateMemberLocations={updateMemberLocations}
      />
    </div>
  );
};

export default MemberLocationForm;
